/**
 * 双色球彩票工具函数
 */

/**
 * 随机生成一注双色球号码
 * @returns { redBalls: string[], blueBall: string }
 */
export function randomPickSSQ() {
  // 红球：1-33中随机不重复选6个，按从小到大排序
  const redPool = Array.from({ length: 33 }, (_, i) => i + 1);
  const redBalls = [];
  for (let i = 0; i < 6; i++) {
    const randomIndex = Math.floor(Math.random() * redPool.length);
    redBalls.push(redPool.splice(randomIndex, 1)[0]);
  }
  redBalls.sort((a, b) => a - b);

  // 蓝球：1-16中随机选1个
  const blueBall = Math.floor(Math.random() * 16) + 1;

  return {
    redBalls: redBalls.map(n => String(n).padStart(2, '0')),
    blueBall: String(blueBall).padStart(2, '0')
  };
}

/**
 * 计算匹配的红球数量
 * @param {string[]} betRed 投注红球
 * @param {string[]} winRed 开奖红球
 * @returns {number} 匹配数量
 */
export function countRedMatch(betRed, winRed) {
  if (!betRed || !winRed) return 0;
  return betRed.filter(n => winRed.includes(n)).length;
}

/**
 * 计算是否命中蓝球
 * @param {string} betBlue 投注蓝球
 * @param {string} winBlue 开奖蓝球
 * @returns {number} 1命中，0未命中
 */
export function countBlueMatch(betBlue, winBlue) {
  if (!betBlue || !winBlue) return 0;
  return betBlue === winBlue ? 1 : 0;
}

/**
 * 判断双色球中奖等级
 * @param {string[]} betRedBalls 投注红球
 * @param {string} betBlueBall 投注蓝球
 * @param {string[]} winRedBalls 开奖红球
 * @param {string} winBlueBall 开奖蓝球
 * @returns { level: number, name: string, prize: number, matchRed: number, matchBlue: number }
 */
export function checkSSQWinning(betRedBalls, betBlueBall, winRedBalls, winBlueBall) {
  const matchRed = countRedMatch(betRedBalls, winRedBalls);
  const matchBlue = countBlueMatch(betBlueBall, winBlueBall);

  // 中奖等级规则
  const rules = [
    { red: 6, blue: 1, level: 1, name: '一等奖', prize: 5000000 },
    { red: 6, blue: 0, level: 2, name: '二等奖', prize: 100000 },
    { red: 5, blue: 1, level: 3, name: '三等奖', prize: 3000 },
    { red: 5, blue: 0, level: 4, name: '四等奖', prize: 200 },
    { red: 4, blue: 1, level: 4, name: '四等奖', prize: 200 },
    { red: 4, blue: 0, level: 5, name: '五等奖', prize: 10 },
    { red: 3, blue: 1, level: 5, name: '五等奖', prize: 10 },
    { red: 2, blue: 1, level: 6, name: '六等奖', prize: 5 },
    { red: 1, blue: 1, level: 6, name: '六等奖', prize: 5 },
    { red: 0, blue: 1, level: 6, name: '六等奖', prize: 5 },
  ];

  const match = rules.find(r => r.red === matchRed && r.blue === matchBlue);

  return {
    level: match ? match.level : null,
    name: match ? match.name : '未中奖',
    prize: match ? match.prize : 0,
    matchRed,
    matchBlue
  };
}

/**
 * 格式化金额显示
 * @param {number} amount 金额（分）
 * @returns {string} 格式化后的金额
 */
export function formatPrize(amount) {
  if (amount >= 10000) {
    return (amount / 100).toLocaleString('zh-CN');
  }
  return (amount / 100).toFixed(2);
}

/**
 * 生成订单号
 * @returns {string} 订单号
 */
export function generateOrderNo() {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `SSQ${timestamp}${random}`;
}

/**
 * 模拟彩票列表数据（用于展示）
 */
export function mockLotteryList() {
  const now = new Date();
  const list = [];

  // 生成一些历史记录
  for (let i = 0; i < 10; i++) {
    const pick = randomPickSSQ();
    const isDrawn = i > 2; // 前几个已开奖
    const won = isDrawn && Math.random() > 0.7;

    let winningNumbers = null;
    if (isDrawn) {
      winningNumbers = randomPickSSQ();
    }

    list.push({
      orderNo: generateOrderNo(),
      period: `2024${String(88 - i).padStart(2, '0')}`,
      redBalls: pick.redBalls,
      blueBall: pick.blueBall,
      betAmount: 200, // 2元
      betTime: new Date(now.getTime() - i * 3600000 * 2).toISOString(),
      status: isDrawn ? (won ? 'won' : 'lost') : 'pending', // pending待开奖, won中奖, lost未中
      winningNumbers: winningNumbers,
      prize: won ? [0, 5, 10, 200, 3000][Math.floor(Math.random() * 5)] * 100 : 0
    });
  }

  return list;
}

/**
 * 计算倒计时
 * @param {string} endTime 结束时间
 * @returns { hours: number, minutes: number, seconds: number }
 */
export function calculateCountdown(endTime) {
  const now = new Date().getTime();
  const end = new Date(endTime).getTime();
  const diff = end - now;

  if (diff <= 0) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { hours, minutes, seconds };
}

/**
 * 计算中奖结果
 * 双色球中奖规则：
 * 一等奖：6红+1蓝
 * 二等奖：6红+0蓝
 * 三等奖：5红+1蓝
 * 四等奖：5红+0蓝 或 4红+1蓝
 * 五等奖：4红+0蓝 或 3红+1蓝
 * 六等奖：2红+1蓝 或 1红+1蓝 或 0红+1蓝
 * 
 * @param {string[]} betRed 投注红球
 * @param {string} betBlue 投注蓝球
 * @param {string[]} winRed 开奖红球
 * @param {string} winBlue 开奖蓝球
 * @returns { level: number, levelName: string, prize: number, redMatch: number, blueMatch: boolean }
 */
export function calculatePrize(betRed, betBlue, winRed, winBlue) {
  const redMatch = countRedMatch(betRed, winRed);
  const blueMatch = betBlue === winBlue;

  let level = 0;
  let levelName = '未中奖';
  let prize = 0;

  // 一等奖：6红+1蓝
  if (redMatch === 6 && blueMatch) {
    level = 1;
    levelName = '一等奖';
    prize = 5000000; // 500万
  }
  // 二等奖：6红+0蓝
  else if (redMatch === 6 && !blueMatch) {
    level = 2;
    levelName = '二等奖';
    prize = 200000; // 20万
  }
  // 三等奖：5红+1蓝
  else if (redMatch === 5 && blueMatch) {
    level = 3;
    levelName = '三等奖';
    prize = 3000; // 3000元
  }
  // 四等奖：5红+0蓝 或 4红+1蓝
  else if ((redMatch === 5 && !blueMatch) || (redMatch === 4 && blueMatch)) {
    level = 4;
    levelName = '四等奖';
    prize = 200; // 200元
  }
  // 五等奖：4红+0蓝 或 3红+1蓝
  else if ((redMatch === 4 && !blueMatch) || (redMatch === 3 && blueMatch)) {
    level = 5;
    levelName = '五等奖';
    prize = 10; // 10元
  }
  // 六等奖：2红+1蓝 或 1红+1蓝 或 0红+1蓝
  else if ((redMatch === 2 && blueMatch) || (redMatch === 1 && blueMatch) || (redMatch === 0 && blueMatch)) {
    level = 6;
    levelName = '六等奖';
    prize = 5; // 5元
  }

  return { level, levelName, prize, redMatch, blueMatch };
}
